import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { resolveMetadata, SITE_BASE_URL } from '../data/routeMetadata';

/**
 * SEO — Single source-of-truth meta tag renderer.
 *
 * How it works:
 *   1. If you render <SEO /> with no props, it looks up the current route in
 *      /src/data/routeMetadata.js and renders those meta tags.
 *   2. If you pass title / description / keywords / image / type / canonical,
 *      those override the resolved entry (backward compat).
 *   3. Every route gets a canonical URL automatically.
 *
 * This makes it impossible for the visible page to describe one thing and the
 * meta tags to describe another — both now flow from the same file.
 */

// Organization schema for AI search engines and LLMs
const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Kaiya Solutions',
  url: SITE_BASE_URL,
  description:
    'AI-powered compliance and risk infrastructure for governments, tribal councils, nonprofits, and enterprises. Purpose-built modules for federal grant compliance, vendor due diligence, contractor verification, governance records, and customs compliance — on one unified evidence ledger.',
  slogan: 'Documentation. Verification. Audit Defensibility.',
  industry: 'Compliance & Risk Management Software',
  sameAs: [],
  knowsAbout: [
    'Risk Infrastructure',
    'Operational Evidence Systems',
    'Audit Defensibility',
    'Federal Grant Compliance',
    'Vendor Due Diligence',
    'Contractor Verification',
    'Governance Records',
    'Customs Compliance',
    'Chargeback Defense',
  ],
  offers: [
    { '@type': 'Offer', name: 'GrantGuard',    description: 'Federal grant compliance under 2 CFR 200' },
    { '@type': 'Offer', name: 'VendorGuard',   description: 'Vendor due diligence and COI management' },
    { '@type': 'Offer', name: 'PROOF',         description: 'Contractor job completion verification' },
    { '@type': 'Offer', name: 'CouncilGuard',  description: 'Governance records for councils and boards' },
    { '@type': 'Offer', name: 'ClerkAssist',   description: 'AI meeting minutes generator' },
    { '@type': 'Offer', name: 'ManifestGuard', description: 'Import/export and customs compliance' },
    { '@type': 'Offer', name: 'ECom Protect',  description: 'Chargeback defense and revenue recovery' },
  ],
};

const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type,
  canonical,
  includeOrgSchema = false,
}) => {
  const { pathname } = useLocation();

  // Resolve metadata: route-based lookup, with prop overrides.
  const meta = resolveMetadata(pathname, {
    title,
    description,
    keywords,
    image,
    url,
    type,
    canonical,
  });

  // Title-suffix policy: any explicit title without "Kaiya" gets "| Kaiya Solutions" appended.
  const finalTitle =
    title && !title.includes('Kaiya')
      ? `${title} | Kaiya Solutions`
      : meta.title;

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={meta.description} />
      <meta name="keywords" content={meta.keywords} />
      <link rel="canonical" href={meta.canonical} />

      {/* Open Graph */}
      <meta property="og:type" content={meta.type} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:image" content={meta.image} />
      <meta property="og:url" content={meta.url} />
      <meta property="og:site_name" content="Kaiya Solutions" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={meta.image} />

      {/* Organization Schema for AI/LLM classification */}
      {includeOrgSchema && (
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
